import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { unitMapping } from '@sorare/core/src/lib/style';

import {
  BATTING_STAT_KEYS,
  PITCHING_STAT_KEYS,
  longBattingStatMessages,
  longPitchingStatMessages,
  shortBattingStatMessages,
  shortPitchingStatMessages,
} from '@sorare/marketplace/src/shared/stats/lib';

import {
  StatsTableCell as Cell,
  StatsTable,
  StatsTableHeader,
} from '@sorare/gameplay/src/atoms/StatsTable';
import { mlbGlossary } from '@sorare/gameplay/src/lib/baseball';

import { messages } from 'components/game/GameBoxScore/GameBoxPlayersTable/message';

import { PlayerRow } from './PlayerRow';
import type {
  GameBoxPlayersTable_BaseballPlayer,
  GameBoxPlayersTable_GameOfBaseball,
  GameBoxPlayersTable_So5Appearance,
} from './__generated__/index.graphql';

const Label = styled(LabelS)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCell = styled(Cell)`
  &.leftAlign {
    text-align: start;

    ${Label} {
      justify-content: flex-start;
    }
  }
`;

const PITCHING_COLUMN_DEFS = [
  ...PITCHING_STAT_KEYS.map(key => {
    return {
      key,
      title: shortPitchingStatMessages[key],
      tooltip: longPitchingStatMessages[key],
    };
  }),
];

const BATTING_COLUMN_DEFS = [
  ...BATTING_STAT_KEYS.map(key => {
    return {
      key,
      title: shortBattingStatMessages[key] || glossary.accept,
      tooltip: longBattingStatMessages[key],
    };
  }),
];

type Props = {
  fantasyScore?: boolean;
  cardsInLineup?: {
    anyPlayer?: GameBoxPlayersTable_BaseballPlayer;
    cards?: GameBoxPlayersTable_So5Appearance[];
  }[];
  forceShowAllPlayers?: boolean;
  position?: 'pitching' | 'batting';
  game: GameBoxPlayersTable_GameOfBaseball;
};

export const GameBoxPlayersTable = ({
  game,
  cardsInLineup = [],
  position,
  fantasyScore,
  forceShowAllPlayers = false,
}: Props) => {
  const sortedByScore = sortBy(
    cil => {
      const playerGameScore =
        position === 'pitching'
          ? cil.anyPlayer?.pitcherPlayerGameScore
          : cil.anyPlayer?.hitterPlayerGameScore;

      if (!playerGameScore) {
        return -1500;
      }

      const score =
        playerGameScore.scoreStatus === ScoreStatus.DID_NOT_PLAY
          ? -1000
          : playerGameScore.score;
      return score;
    },
    [...cardsInLineup]
  ).reverse();

  const showPlayers = sortedByScore.filter(cil => {
    const playerGameScore =
      position === 'pitching'
        ? cil.anyPlayer?.pitcherPlayerGameScore
        : cil.anyPlayer?.hitterPlayerGameScore;

    if (
      !forceShowAllPlayers &&
      !playerGameScore?.anyPlayerGameStats?.onGameSheet
    ) {
      return false;
    }

    return playerGameScore?.scoreStatus !== ScoreStatus.DID_NOT_PLAY;
  });

  // we scale stat column widths so that other columns have a consistent width
  const appliedRatio =
    (position === 'pitching' ? PITCHING_COLUMN_DEFS : BATTING_COLUMN_DEFS)
      .length /
    Math.max(PITCHING_COLUMN_DEFS.length, BATTING_COLUMN_DEFS.length);

  const statCellWidth = 40 / appliedRatio;

  const header = [
    {
      key: 'identity',
      title:
        position === 'pitching' ? mlbGlossary.pitchers : mlbGlossary.batters,
      width: '200px',
    },
    {
      key: 'total',
      title: messages.total,
      tooltip: messages.totalLong,
      width: unitMapping[7],
    },
    ...(position === 'pitching'
      ? PITCHING_COLUMN_DEFS
      : BATTING_COLUMN_DEFS
    ).map(h => ({ ...h, width: `${statCellWidth}px` })),
  ];

  return (
    <StatsTable>
      <StatsTableHeader>
        {header.map(({ key, title, tooltip, width }) => (
          <StyledCell
            key={key}
            title={tooltip}
            className={classNames({ leftAlign: key === 'identity' })}
            width={width}
          >
            <Label color="var(--c-nd-600)" uppercase>
              {typeof title === 'string' ? (
                title
              ) : (
                <FormattedMessage {...title} />
              )}
            </Label>
          </StyledCell>
        ))}
      </StatsTableHeader>
      <tbody>
        {showPlayers.map(
          cardInLineup =>
            cardInLineup.anyPlayer?.slug && (
              <PlayerRow
                key={cardInLineup.anyPlayer.slug}
                game={game}
                fantasyScore={fantasyScore}
                position={position}
                player={cardInLineup.anyPlayer}
              />
            )
        )}
      </tbody>
    </StatsTable>
  );
};

GameBoxPlayersTable.fragments = {
  So5Appearance: gql`
    fragment GameBoxPlayersTable_So5Appearance on So5Appearance {
      id
      ...PlayerRow_So5Appearance
    }
    ${PlayerRow.fragments.So5Appearance}
  ` as TypedDocumentNode<GameBoxPlayersTable_So5Appearance>,
  GameOfBaseball: gql`
    fragment GameBoxPlayersTable_GameOfBaseball on GameOfBaseball {
      id
      ...PlayerRow_GameOfBaseball
    }
    ${PlayerRow.fragments.GameOfBaseball}
  ` as TypedDocumentNode<GameBoxPlayersTable_GameOfBaseball>,
  BaseballPlayer: gql`
    fragment GameBoxPlayersTable_BaseballPlayer on BaseballPlayer {
      slug
      pitcherPlayerGameScore(gameId: $gameIdWithoutPrefix) {
        id
        score
        scoreStatus
        anyPlayerGameStats {
          id
          onGameSheet
        }
      }
      hitterPlayerGameScore(gameId: $gameIdWithoutPrefix) {
        id
        score
        scoreStatus
        anyPlayerGameStats {
          id
          onGameSheet
        }
      }
      ...PlayerRow_BaseballPlayer
    }
    ${PlayerRow.fragments.BaseballPlayer}
  ` as TypedDocumentNode<GameBoxPlayersTable_BaseballPlayer>,
};
